import { Col, Container, Row, Spinner } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import { useParams } from "react-router-dom";
import { useVisitor } from "../api/hooks";
import QRCode from "qrcode.react";
import { LocalDate } from "./LocalDateTime";

// Temporary interface based on DTO structure until API client is fixed
interface VisitorType {
  id: string;
  uid?: string;
  siteId?: string;
  siteName?: string;
  siteAddress?: string;
  siteGracePeriod?: number;
  lgaName?: string;
  timeZone?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  city?: string;
  status?: string;
  error?: string;
  visitorName?: string;
  vehiclePlate?: string;
  visitorPhone?: string;
  type?: string;
}

export const Visitor = () => {
  const { id } = useParams();
  const { data: visitorData, isLoading, error } = useVisitor(id!);

  console.log(visitorData?.data);

  // Cast to expected type until API client is fixed
  const visitor = visitorData?.data as VisitorType | undefined;

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <Spinner animation="border" role="status" />
          <div className="mt-2">Loading your reservation...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100 px-3">
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4>Error loading reservation</h4>
            <p className="mb-0">{error.message}</p>
          </div>
        </div>
      </Container>
    );
  }

  if (!visitor) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100 px-3">
        <div className="text-center">
          <div className="alert alert-warning" role="alert">
            <h4>Reservation not found</h4>
            <p className="mb-0">Please check your visitor ID and try again.</p>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-3 px-3" style={{ maxWidth: "500px" }}>
      {/* Header with logo */}
      <Row className="justify-content-center mb-4">
        <Col xs="auto">
          <Image
            src={"/images/sharepark-logo.svg"}
            width="150"
            className="img-fluid"
            alt="SharePark Logo"
          />
        </Col>
      </Row>

      {/* Main content */}
      <Row>
        <Col>
          <div className="text-center mb-4">
            <h2 className="h4 mb-3">Parking Reservation Confirmed</h2>

            {/* Status badge */}
            <div className="mb-3">
              <span
                className={`badge ${
                  visitor.status === "Confirmed" ? "bg-success" : "bg-primary"
                } fs-6`}
              >
                {visitor.status || "Confirmed"}
              </span>
            </div>

            {/* QR Code */}
            <div className="card mb-4">
              <div className="card-body">
                <h6 className="card-title mb-3">Entry QR Code</h6>
                <div className="d-flex justify-content-center mb-3">
                  <QRCode
                    value={id || visitor.id}
                    size={200}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"M"}
                    includeMargin={true}
                    renderAs={"svg"}
                  />
                </div>
                <small className="text-muted">
                  Scan this code at the parking site entrance
                </small>
              </div>
            </div>

            {/* Visitor details for strata bookings */}
            {(visitor.visitorName || visitor.vehiclePlate) && (
              <div className="card mb-4">
                <div className="card-body">
                  <h6 className="card-title">Visitor Details</h6>
                  <div className="row text-center">
                    {visitor.visitorName && (
                      <div className="col">
                        <div className="text-muted small">Name</div>
                        <div className="fw-medium">{visitor.visitorName}</div>
                      </div>
                    )}
                    {visitor.vehiclePlate && (
                      <div className="col">
                        <div className="text-muted small">Plate</div>
                        <div className="fw-medium">{visitor.vehiclePlate}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Site information */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title text-primary">
                  {visitor.siteName || "Unknown Site"}
                </h5>
                <p className="card-text text-muted mb-0">
                  {visitor.siteAddress || "Address not available"}
                </p>
                {visitor.city && (
                  <small className="text-muted">{visitor.city}</small>
                )}
              </div>
            </div>

            {/* Time information */}
            <div className="card mb-4">
              <div className="card-body">
                <h6 className="card-title">Reservation Time</h6>
                <div className="row text-center">
                  <div className="col-6">
                    <div className="text-success">
                      <strong>From</strong>
                    </div>
                    <div>
                      <LocalDate date={visitor.startDate} />
                    </div>
                    <div className="text-muted small">
                      {visitor.startTime || "-"}
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-danger">
                      <strong>Until</strong>
                    </div>
                    <div>
                      <LocalDate date={visitor.endDate} />
                    </div>
                    <div className="text-muted small">
                      {visitor.endTime || "-"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions */}
            {visitor.siteGracePeriod != null && visitor.siteGracePeriod > 0 && (
              <div className="alert alert-info">
                <small>
                  <strong>Tip:</strong> You can arrive up to{" "}
                  <strong>{visitor.siteGracePeriod} minutes</strong> before your
                  reservation time. Follow the on-site instructions to find your
                  bay.
                </small>
              </div>
            )}

            {/* Error message if any */}
            {visitor.error && (
              <div className="alert alert-warning">
                <small>{visitor.error}</small>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};
//
